import React, { useMemo } from 'react';
import { Button, Grid } from '@mui/material';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import BreadCrumbs from '../common/BreadCrumbs';
import ReusableTabs from '../common/ReusableTabs';
import * as styles from '../productPage/tableComponents/ProductPage_style';
import {
    GET_RESOURCE_LIBRARY_NEW_PRODUCT_INTRODUCTION_ITEM_QUERY,
    getResourceLibraryNewProductIntroductionItemQueryVariables,
} from '../../graphql/pimcore/modules/resourceLibrary/queries/getResourceLibraryNewProductIntroductionItemQuery';
import pimcoreClient from '../../graphql/pimcore/apollo/pimcoreClient';
import { transformResourceLibraryNewProductIntroductionItemResponse } from '../../graphql/pimcore/modules/resourceLibrary/tools/transformResourceLibraryNewProductIntroductionItemResponse';
import { ResourceLibraryNewProductIntroductionItem } from '../../graphql/pimcore/modules/resourceLibrary/types/resourceLibraryItems';
import { findLocale, LocaleCode } from '../../utils/Locale';
import { PIMCORE_CONTENT_BASE_URL } from '../../utils/Pimcore';
import DebouncedLoader from '../common/DebouncedLoader';
import { fixPimcoreRootRelativeSrcs } from '../../utils/htmlUtils';
import { useProductGroups } from '../../graphql/pimcore/modules/resourceLibrary/contexts/ProductGroupsProvider';
import {
    getResourceLibraryItemOpenUrl,
    openResourceLibraryItem,
} from '../../utils/openResourceLibraryItem';
import { loadResourceLibraryItemById } from '../../graphql/pimcore/modules/resourceLibrary/tools/loadResourceLibraryItemById';
import { useLocale } from '../../contexts/LocaleProvider';
import { useCommonContent } from '../../graphql/pimcore/modules/siteContent/hooks/useCommonContent';
import { injectLink } from '../../graphql/pimcore/common/tools/injectLink';
import { useItemHeaderBackground } from '../../graphql/pimcore/modules/resourceLibrary/hooks/useItemHeaderBackground';
import StructuredSection, { structuredSectionStyles } from './structuredSections/StructuredSection';
import { joinClassNames } from './structuredSections/utils';
import { toUrl } from '@/utils/convertImagePath';

interface NpiResourcePageProps {
    id: string;
    locale: string;
}

const sectionClasses = structuredSectionStyles;

const NewProductIntroductionResourcePage: React.FC<NpiResourcePageProps> = ({ id, locale }) => {
    const localeCode: LocaleCode = findLocale(locale).locale;
    const { productGroupById } = useProductGroups();
    const navigate = useNavigate();
    const { locale: spaLocale } = useLocale();
    const { contentTree: cmsCommonResourceLibraryContent } = useCommonContent('ResourceLibrary');
    const cmsContent = cmsCommonResourceLibraryContent.itemClasses.NewProductIntroductionItem;

    // Query for the NPI item
    const {
        loading: isItemLoading,
        error: itemError,
        data: itemData,
    } = useQuery(GET_RESOURCE_LIBRARY_NEW_PRODUCT_INTRODUCTION_ITEM_QUERY, {
        variables: getResourceLibraryNewProductIntroductionItemQueryVariables(
            localeCode,
            Number(id)
        ),
        client: pimcoreClient,
    });

    React.useEffect(() => {
        if (itemError) {
            console.error('GraphQL NPI Query Error:', itemError);
        }
    }, [itemError]);

    // Transform the raw response
    const npiItem = useMemo<ResourceLibraryNewProductIntroductionItem | null>(() => {
        if (!itemData) return null;
        const item = transformResourceLibraryNewProductIntroductionItemResponse(itemData);
        if (!item) return item;
        return {
            ...item,
            body: fixPimcoreRootRelativeSrcs(item.body, true),
        };
    }, [itemData]);

    const isLoading = isItemLoading;

    // Redirect to 404 on error or when item could not be validated/loaded
    React.useEffect(() => {
        if (!isLoading && (itemError || !npiItem)) {
            navigate('/404_error', { replace: true });
        }
    }, [isLoading, itemError, npiItem, navigate]);

    const headerBackgroundImage = useItemHeaderBackground({
        item: npiItem,
        productGroupById,
        fallbackPath: cmsContent.defaultPageTitleBackgroundImage.imageUrl,
    });

    // Load brief item to compute right-clickable href
    const [briefHref, setBriefHref] = React.useState<string>('#');
    React.useEffect(() => {
        let cancelled = false;
        (async () => {
            if (!npiItem?.productBrief.id) {
                if (!cancelled) setBriefHref('#');
                return;
            }
            const loaded = await loadResourceLibraryItemById({
                apolloClient: pimcoreClient,
                kind: npiItem.productBrief.kind,
                id: npiItem.productBrief.id,
                locale: localeCode,
            });
            if (!cancelled) {
                const url = loaded
                    ? getResourceLibraryItemOpenUrl({ item: loaded, lang: localeCode })
                    : '#';
                setBriefHref(url || '#');
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [npiItem?.productBrief.id, npiItem?.productBrief.kind, localeCode]);

    const openBrief = async (e?: React.MouseEvent) => {
        if (e) e.preventDefault();
        if (!npiItem?.productBrief.id) return;
        const loaded = await loadResourceLibraryItemById({
            apolloClient: pimcoreClient,
            kind: npiItem.productBrief.kind,
            id: npiItem.productBrief.id,
            locale: localeCode,
        });
        if (loaded) {
            const lang = spaLocale ?? localeCode;
            openResourceLibraryItem({ item: loaded, lang });
        }
    };

    // headline with optional link injection (render link as RouterLink but we intercept click)
    const headlineNode = useMemo(() => {
        if (!npiItem) return '';
        return injectLink(npiItem.headline, {
            placeholderTag: 'link-to-brief',
            href: briefHref,
            useRouter: false,
        });
    }, [npiItem, briefHref]);

    const contactLocalSalesStatementNode = useMemo(() => {
        return injectLink(cmsContent.contactLocalSalesStatement.content, {
            placeholderTag: 'link',
            href: '/SalesResources/SalesOffices',
            useRouter: true,
        });
    }, [cmsContent.contactLocalSalesStatement.content]);

    const coverImageUrl = npiItem?.coverImageUrl
        ? `${PIMCORE_CONTENT_BASE_URL}${npiItem.coverImageUrl}`
        : null;

    const heroSection = (
        <StructuredSection background="gray">
            <div className={joinClassNames(sectionClasses.groupContent, sectionClasses.groupHero)}>
                {coverImageUrl && (
                    <div
                        className={joinClassNames(
                            sectionClasses.groupHeroImage,
                            sectionClasses.groupImage,
                            sectionClasses.sectionBlock
                        )}
                    >
                        <img src={coverImageUrl} alt={npiItem?.name ?? ''} />
                    </div>
                )}
                <div className={sectionClasses.groupHeroContent}>
                    <div className={sectionClasses.sectionBlock}>
                        <h1>
                            <span
                                onClick={(e) => {
                                    const target = e.target as HTMLElement;
                                    if (target && target.closest('a')) {
                                        openBrief(e);
                                    }
                                }}
                            >
                                {headlineNode}
                            </span>
                        </h1>
                        {npiItem?.bodyParagraphs.map((p, i) => <p key={`p-${i}`}>{p}</p>)}
                    </div>
                    {npiItem?.quickFacts?.length ? (
                        <div className={sectionClasses.sectionBlock}>
                            <h1>{cmsContent.quickFactsSectionLabel.content}</h1>
                            <ul>
                                {npiItem.quickFacts.map((t, i) => (
                                    <li key={`qf-${i}`}>{t}</li>
                                ))}
                            </ul>
                        </div>
                    ) : null}
                </div>
            </div>
        </StructuredSection>
    );

    const featuresSection = (
        <StructuredSection background="white">
            <div className={sectionClasses.groupContent}>
                <Grid container spacing={0}>
                    <Grid item xs={12} md={6}>
                        <div className={sectionClasses.sectionBlock}>
                            <h1>{cmsContent.featuresSectionLabel.content}</h1>
                            <ul>{npiItem?.features.map((t, i) => <li key={`f-${i}`}>{t}</li>)}</ul>
                        </div>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <div className={sectionClasses.sectionBlock}>
                            <h1>{cmsContent.customerValueSectionLabel.content}</h1>
                            <ul>
                                {npiItem?.customerValues.map((t, i) => (
                                    <li key={`cv-${i}`}>{t}</li>
                                ))}
                            </ul>
                        </div>
                    </Grid>
                </Grid>
            </div>
        </StructuredSection>
    );

    const targetApplicationsSection = (
        <StructuredSection background="white">
            <div className={sectionClasses.groupContent}>
                <div className={sectionClasses.sectionBlock}>
                    <h1>{cmsContent.targetApplicationsSectionLabel.content}</h1>
                    <ul className={sectionClasses.listColumns}>
                        {npiItem?.targetApplications.map((t, i) => <li key={`ta-${i}`}>{t}</li>)}
                    </ul>
                </div>
            </div>
        </StructuredSection>
    );

    const marketAdvantagesSection = (
        <StructuredSection background="gray">
            <div className={sectionClasses.groupContent}>
                <div className={sectionClasses.sectionBlock}>
                    <h1>{cmsContent.marketAdvantagesSectionLabel.content}</h1>
                    <ul>{npiItem?.marketAdvantages.map((t, i) => <li key={`ma-${i}`}>{t}</li>)}</ul>
                </div>
            </div>
        </StructuredSection>
    );

    const buttonsSection = (
        <StructuredSection background="gray">
            <div className={sectionClasses.groupContent}>
                <div className={sectionClasses.sectionBlock}>
                    <div className={sectionClasses.sectionButtons}>
                        <Button
                            component="a"
                            href={briefHref}
                            onClick={openBrief}
                            variant="contained"
                            className={sectionClasses.sectionButton}
                        >
                            {cmsContent.productBriefButtonLabel.content}
                        </Button>
                        <Button
                            variant="contained"
                            className={sectionClasses.sectionButton}
                            href={npiItem?.similarProductsRoute || '#'}
                            target="_blank"
                        >
                            {cmsContent.similarProductsButtonLabel.content}
                        </Button>
                    </div>
                </div>
            </div>
        </StructuredSection>
    );

    const closingSection = (
        <StructuredSection background="gray" merged={true}>
            <div className={sectionClasses.groupContent}>
                <div className={sectionClasses.sectionBlock}>
                    <p>
                        {npiItem?.closingSummary} {contactLocalSalesStatementNode}
                    </p>
                    <Button
                        component={RouterLink}
                        variant="contained"
                        className={sectionClasses.sectionButton}
                        to="/SalesResources/ContactUs"
                    >
                        {cmsContent.contactUsButtonLabel.content}
                    </Button>
                </div>
            </div>
        </StructuredSection>
    );

    const sectionsContent = (
        <div className={sectionClasses.root}>
            <div className={sectionClasses.sectionWrapper}>
                {heroSection}
                {featuresSection}
                {targetApplicationsSection}
                {marketAdvantagesSection}
                {buttonsSection}
                {closingSection}
            </div>
        </div>
    );

    return (
        <DebouncedLoader loading={isLoading} delay={1000} size={40} centered={true}>
            <ReusableTabs
                isHtmlAsset={false}
                backgroundColor="#ffffff"
                bannerImage={headerBackgroundImage}
                bannerTitle={npiItem?.name || ''}
                bannerDescription=""
                tabLabels={[]}
                tabKeys={['']}
                tabContents={[sectionsContent]}
                bannerFontStyle={styles.bannerFont}
                subBannerFontStyle={styles.bannerFont2}
                pageName="browseAllProductHome"
                showTabs={false}
                breadcrumbs={
                    <BreadCrumbs navigationPath="/Home/SalesAndResources/ResourceLibrary" />
                }
            />
        </DebouncedLoader>
    );
};

export default NewProductIntroductionResourcePage;
